/**
 * SpecialStatistic (Foundry: rule-element/special-statistic.ts): a statistic of the Entry's own
 * (an impulse attack, a deviant ability DC), usually extending an existing one: the class DC, the
 * class's spellcasting, a skill or a save.
 */
import { isValidPredicate, type RawPredicate } from "../predicate";
import { sluggify } from "../slug";
import { traditionStatistic } from "../spellcasting";
import type { EngineState } from "../state";
import { buildStatistic, type DerivedStatistic, type StatisticSpec } from "../statistics";
import { SAVES, SKILL_SLUGS, isAttribute, type Attribute } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

interface SpecialStatisticData {
  slug: string;
  type: "simple" | "check" | "attack-roll";
  extends: string | null;
  attribute: Attribute | null;
  itemCasting: { predicate: RawPredicate; tradition: string | null } | null;
}

const RESERVED = new Set(["arcane", "divine", "occult", "primal", ...SAVES, "perception", "initiative", ...SKILL_SLUGS]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance, state: EngineState): void {
  const s = rule.source;
  const slug = typeof s.slug === "string" && s.slug ? sluggify(s.slug) : null;
  if (!slug) return rule.fail("slug: must be a non-empty string");
  const classTraits = state.catalogue?.table("classTraits");
  if (RESERVED.has(slug) || (classTraits && slug in classTraits)) return rule.fail(`slug: "${slug}" names an existing statistic`);
  const itemCasting = isPlainObject(s.itemCasting) ? s.itemCasting : null;
  if (itemCasting && !isValidPredicate(itemCasting.predicate)) return rule.fail("itemCasting.predicate: must be a predicate");
  const type = s.type === undefined ? (itemCasting ? "attack-roll" : "check") : s.type;
  if (type !== "simple" && type !== "check" && type !== "attack-roll") return rule.fail(`type: "${String(type)}" is not simple, check or attack-roll`);
  if (s.attribute !== undefined && s.attribute !== null && !isAttribute(s.attribute)) return rule.fail(`attribute: "${String(s.attribute)}" is not an attribute`);
  rule.slug = slug;
  rule.data = {
    slug,
    type,
    extends: typeof s.extends === "string" && s.extends ? s.extends : null,
    attribute: (s.attribute as Attribute | null | undefined) ?? null,
    itemCasting: itemCasting ? { predicate: itemCasting.predicate as RawPredicate, tradition: typeof itemCasting.tradition === "string" ? itemCasting.tradition : null } : null,
  } satisfies SpecialStatisticData as unknown as Record<string, unknown>;
}

/** Foundry's `actor.getStatistic(slug)`, over what this engine derives. */
function findStatistic(state: EngineState, slug: string): DerivedStatistic | null {
  const derived = state.derived;
  if (!derived) return null;
  if (slug === "class") {
    const primary = Object.entries(state.actor.system.proficiencies.classDCs).find(([, c]) => c.primary)?.[0];
    return primary ? (derived.classDCs[primary] ?? null) : null;
  }
  if (slug === "class-spell") {
    // Foundry: the best of the class DCs and the Spellcasting Entries' statistics.
    const candidates = [...Object.values(derived.classDCs), ...Object.values(derived.spellcastingEntries)];
    return candidates.sort((a, b) => b.check.mod - a.check.mod)[0] ?? derived.spellcasting;
  }
  if (slug === "base-spellcasting") return derived.spellcasting;
  if (slug === "arcane" || slug === "divine" || slug === "occult" || slug === "primal") return traditionStatistic(state, slug);
  for (const [id, statistic] of Object.entries(derived.spellcastingEntries)) if (slug === id || slug === statistic.slug) return statistic;
  if (slug === "perception") return derived.perception;
  if (slug === "initiative") return derived.initiative;
  if (slug === "ac" || slug === "armor") return derived.ac;
  if ((SAVES as readonly string[]).includes(slug)) return derived.saves[slug as (typeof SAVES)[number]];
  return derived.skills[slug] ?? derived.classDCs[slug] ?? state.synthetics.statistics.get(slug) ?? null;
}

function afterPrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as SpecialStatisticData;
  if (data.type === "simple") return;
  const extendedFrom = data.extends ? findStatistic(state, data.extends) : null;
  if (data.extends && !extendedFrom) return rule.fail(`extends: unable to resolve statistic "${data.extends}"`);
  const label = data.itemCasting ? (extendedFrom?.label ?? rule.label) : rule.label;
  const checkType = data.type === "check" ? "check" : "attack-roll";
  const checkDomains = data.type === "check" ? [`${data.slug}-check`] : [`${data.slug}-attack-roll`];
  const dcDomains = [`${data.slug}-dc`];
  const domains = [data.slug];
  if (data.itemCasting) {
    domains.push("spell-attack-dc");
    checkDomains.push("spell-attack", "spell-attack-roll");
    dcDomains.push("spell-dc");
    if (data.itemCasting.tradition) {
      checkDomains.push(`${data.itemCasting.tradition}-spell-attack`);
      dcDomains.push(`${data.itemCasting.tradition}-spell-dc`);
    }
  }
  const base = extendedFrom?.spec;
  const spec: StatisticSpec = {
    slug: data.slug,
    label: state.catalogue?.localize(label) ?? label,
    attribute: data.attribute ?? base?.attribute ?? null,
    rank: base?.rank ?? null,
    domains: [...new Set([...(base?.domains ?? []), ...domains])],
    modifiers: base?.modifiers ?? [],
    check: { type: checkType, domains: [...new Set([...(base?.check?.domains ?? []), ...checkDomains])], modifiers: base?.check?.modifiers ?? [] },
    dc: { domains: [...new Set([...(base?.dc?.domains ?? [`${base?.slug}-dc`]), ...dcDomains])].filter((d) => d !== "undefined-dc"), modifiers: base?.dc?.modifiers ?? [] },
    rollOptions: [...(base?.rollOptions ?? []), ...(extendedFrom ? [`check:statistic:base:${extendedFrom.slug}`] : [])],
  };
  const statistic = buildStatistic(state, spec);
  state.synthetics.statistics.set(data.slug, statistic);
}

export const SpecialStatisticHandler: RuleHandler = { key: "SpecialStatistic", parse, afterPrepareData };
